import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import { Button, useLocalization, Modal, IconRefresh } from '@magicdoorinc/ui';
import { createSignal, createEffect, Show } from 'solid-js';
import JsonHighlighter from '~/components/common/JsonHighlighter';
import { auth } from '~/config/auth';
import type { Component } from 'solid-js';

interface AuthLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AuthLogsModal: Component<AuthLogsModalProps> = (props) => {
  const { t } = useLocalization();
  const [copied, setCopied] = createSignal(false);
  const [logs, setLogs] = createSignal<any[]>([]);

  const loadLogs = () => {
    try {
      const authLogs = auth.logger.getLogs();
      setLogs(authLogs);
    } catch (error) {
      console.error('Failed to get auth logs:', error);
    }
  };

  createEffect(() => {
    if (props.isOpen) {
      loadLogs();
    }
  });

  const formatLogs = () => {
    return JSON.stringify(logs(), null, 2);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(formatLogs());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy logs:', err);
    }
  };

  const downloadLogs = () => {
    const blob = new Blob([formatLogs()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `auth-logs-${todayWithSystemTimeZone().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      visible={props.isOpen}
      footer={
        <div class="flex justify-end gap-2 border-t border-separator p-6">
          <Button variant="outlined" size="sm" onClick={loadLogs} class="flex items-center gap-2">
            <IconRefresh class="size-4" />
            {t('Refresh')}
          </Button>
          <Button variant="outlined" size="sm" onClick={copyToClipboard} class="flex items-center gap-2">
            <Show when={copied()} fallback={t('Copy')}>
              {t('Copied!')}
            </Show>
          </Button>
          <Button size="sm" onClick={downloadLogs} class="flex items-center gap-2">
            {t('Download')}
          </Button>
        </div>
      }
      onCancel={props.onClose}
      title={t('Auth Logs')}
      size="lg"
      showCloseButton={true}>
      <div class="mb-4 space-y-4">
        <JsonHighlighter data={[...logs()].reverse()} maxHeight="48rem" maxWidth="80vw" />

        <div class="px-6 text-xs text-gray-500">
          {t('Total logs')}: {logs().length}
        </div>
      </div>
    </Modal>
  );
};

export default AuthLogsModal;
